import React, { useState, useEffect } from 'react';
import './QuestionModal.css';

/**
 * 问题数据类型
 */
interface Question {
  id: string;
  question: string;
}

/**
 * QuestionModal 组件
 * 
 * 功能：
 * - 显示随机问题
 * - 验证用户答案
 * - 根据验证结果返回权限状态
 * 
 * 权限说明：
 * - 'full': 可以添加和编辑节点
 * - 'view': 只能浏览，不能编辑
 */

interface QuestionModalProps {
  onComplete: (permission: 'full' | 'view') => void; // 完成回调，返回权限状态
}

const QuestionModal: React.FC<QuestionModalProps> = ({ onComplete }) => {
  const [question, setQuestion] = useState<Question | null>(null);
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [showSkip, setShowSkip] = useState(false);
  const [noQuestions, setNoQuestions] = useState(false);

  /**
   * 加载随机问题
   */
  useEffect(() => {
    const loadQuestion = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';
        const response = await fetch(`${apiUrl}/questions/random`);
        const data = await response.json();

        if (data.success && data.data) {
          setQuestion(data.data);
        } else {
          // 如果没有问题，设置标志并授予完整权限
          console.log('没有可用的问题，授予完整权限');
          setNoQuestions(true);
          setTimeout(() => {
            onComplete('full');
          }, 1500);
          return;
        }
      } catch (err) {
        console.error('加载问题失败:', err);
        // 网络错误，授予浏览权限
        console.log('加载问题失败，授予浏览权限');
        setError('网络连接失败');
        setNoQuestions(true);
        setTimeout(() => {
          onComplete('view');
        }, 1500);
        return;
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestion();

    // 默认显示跳过选项
    setShowSkip(true);

    return () => {};
  }, [onComplete]);

  /**
   * 验证答案
   */
  const handleVerify = async () => {
    if (!question || !answer.trim()) {
      setError('请输入答案');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';
      const response = await fetch(`${apiUrl}/questions/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questionId: question.id,
          answer: answer.trim(),
        }),
      });

      const data = await response.json();

      if (data.success && data.data?.isCorrect) {
        // 回答正确，授予完整权限
        onComplete('full');
      } else {
        // 回答错误，授予浏览权限
        setError('答案不正确，已授予浏览权限');
        setTimeout(() => {
          onComplete('view');
        }, 1500);
      }
    } catch (err) {
      console.error('验证答案失败:', err);
      setError('验证失败，已授予浏览权限');
      setTimeout(() => {
        onComplete('view');
      }, 1500);
    } finally {
      setIsVerifying(false);
    }
  };

  /**
   * 跳过问题
   */
  const handleSkip = () => {
    onComplete('view');
  };

  if (isLoading) {
    return (
      <div className="question-modal-overlay">
        <div className="question-modal">
          <div className="question-loading">
            <div className="spinner"></div>
            <p>正在加载...</p>
          </div>
        </div>
      </div>
    );
  }

  // 如果没有问题，显示提示
  if (noQuestions) {
    return (
      <div className="question-modal-overlay">
        <div className="question-modal">
          <div className="question-header">
            <h2>无需验证</h2>
          </div>
          <div className="question-content">
            <div className="question-text">
              {error ? '网络连接失败，已授予浏览权限' : '暂无验证问题，已授予完整权限'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="question-modal-overlay">
      <div className="question-modal">
        <div className="question-header">
          <h2>你是谁？</h2>
          <p>回答正确才可以查看我们的秘密哦</p>
        </div>

        <div className="question-content">
          <div className="question-text">{question?.question}</div>
          <input
            type="text"
            className="question-input"
            placeholder="请输入答案"
            value={answer}
            onChange={(e) => {
              setAnswer(e.target.value);
              setError('');
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleVerify();
              }
            }}
            autoFocus
          />
          {error && <div className="question-error">{error}</div>}
        </div>

        <div className="question-actions">
          <button
            className="question-button question-button-primary"
            onClick={handleVerify}
            disabled={isVerifying}
          >
            {isVerifying ? '验证中...' : '芝麻开门'}
          </button>
          {showSkip && (
            <button
              className="question-button question-button-secondary"
              onClick={handleSkip}
            >
              围观一下
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionModal;